// src/components/Budget/SpendingBreakdownChart.js
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  alpha,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Tooltip as MuiTooltip,
  Collapse,
  Button,
  useMediaQuery
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Sector
} from 'recharts';
import {
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Visibility,
  VisibilityOff,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helpers';
import { useCurrency } from '../../contexts/CurrencyContext';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6'];

// Animated active shape for pie chart hover
const renderActiveShape = (props) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle,
    fill, payload, percent
  } = props;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={outerRadius + 12}
        outerRadius={outerRadius + 15}
        fill={fill}
        opacity={0.8}
      />
    </g>
  );
};

export const SpendingBreakdownChart = ({ 
  spendingData = [], 
  totalBudget = 0,
  onMemberClick = null // Optional callback for drill-down
}) => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [viewMode, setViewMode] = useState('amount'); // 'amount' or 'percentage'
  const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'
  const [hiddenMembers, setHiddenMembers] = useState(new Set());
  const [activeIndex, setActiveIndex] = useState(null);
  const [showDetails, setShowDetails] = useState(true);

  // Calculate total spending
  const totalSpending = spendingData.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Prepare data for charts (excluding hidden members)
  const chartData = spendingData
    .map((item, index) => ({
      name: item.userName || 'Unknown User',
      amount: item.amount || 0,
      percentage: totalSpending > 0 ? ((item.amount || 0) / totalSpending * 100).toFixed(1) : 0,
      color: COLORS[index % COLORS.length],
      avatar: item.userAvatar || '',
      userId: item.userId,
      originalIndex: index
    }))
    .filter((item) => !hiddenMembers.has(item.originalIndex));

  // Toggle member visibility
  const toggleMemberVisibility = useCallback((index) => {
    setHiddenMembers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  // Handle member click for drill-down
  const handleMemberClick = useCallback((member) => {
    if (onMemberClick) {
      onMemberClick(member);
    }
  }, [onMemberClick]);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const budgetPercentage = totalBudget > 0 
        ? ((data.amount / totalBudget) * 100).toFixed(1) 
        : 0;
      
      return (
        <Paper
          elevation={8}
          sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.98),
            border: `2px solid ${data.color}`,
            borderRadius: 1.5,
            backdropFilter: 'blur(10px)'
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <Avatar
              src={data.avatar}
              sx={{ width: 28, height: 28, bgcolor: data.color }}
            >
              {data.name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="subtitle2" fontWeight="600">
              {data.name}
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              Amount: <strong style={{ color: theme.palette.text.primary }}>
                {formatCurrency(data.amount)}
              </strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Share: <strong style={{ color: theme.palette.text.primary }}>
                {data.percentage}%
              </strong>
            </Typography>
            {totalBudget > 0 && (
              <Typography variant="body2" color="text.secondary">
                Of Budget: <strong style={{ color: theme.palette.text.primary }}>
                  {budgetPercentage}%
                </strong>
              </Typography>
            )}
          </Stack>
        </Paper>
      );
    }
    return null;
  };

  // Custom label for pie chart
  const renderCustomLabel = (entry) => {
    if (viewMode === 'amount') {
      return formatCurrency(entry.amount);
    }
    return `${entry.percentage}%`;
  };

  // Handle pie chart sector click
  const onPieEnter = useCallback((_, index) => {
    setActiveIndex(index);
  }, []);

  const onPieLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4]
        }
      }}
    >
      {/* Header with controls */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Spending Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {chartData.length === 0 
              ? 'No data to display' 
              : `Total: ${formatCurrency(totalSpending)}${totalBudget > 0 ? ` of ${formatCurrency(totalBudget)}` : ''}`
            }
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {/* View mode toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, value) => value && setViewMode(value)}
            size="small"
            sx={{ height: 36 }}
          >
            <ToggleButton value="amount" aria-label="show amounts">
              <MoneyIcon fontSize="small" sx={{ mr: { xs: 0, sm: 0.5 } }} />
              {!isMobile && 'Amount'}
            </ToggleButton>
            <ToggleButton value="percentage" aria-label="show percentages">
              <PercentIcon fontSize="small" sx={{ mr: { xs: 0, sm: 0.5 } }} />
              {!isMobile && 'Percent'}
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Chart type toggle */}
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(e, value) => value && setChartType(value)}
            size="small"
            sx={{ height: 36 }}
          >
            <ToggleButton value="pie" aria-label="pie chart">
              Pie
            </ToggleButton>
            <ToggleButton value="bar" aria-label="bar chart">
              Bar
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {/* Chart container with animation */}
      <Box 
        sx={{ 
          width: '100%', 
          height: { xs: 300, sm: 350, md: 400 },
          transition: 'height 0.3s ease-in-out'
        }}
      >
        {chartData.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Typography variant="h6" color="text.secondary">
              📊 No spending data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hiddenMembers.size > 0 
                ? 'All members are hidden. Show members to see the chart.'
                : 'Add transactions to see your spending breakdown'
              }
            </Typography>
          </Box>
        ) : chartType === 'pie' ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={isMobile ? 80 : 100}
                fill="#8884d8"
                dataKey={viewMode === 'amount' ? 'amount' : 'percentage'}
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={onPieEnter}
                onMouseLeave={onPieLeave}
                animationBegin={0}
                animationDuration={800}
                animationEasing="ease-out"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                    cursor="pointer"
                    onClick={() => handleMemberClick(entry)}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value, entry) => (
                  <span style={{ color: theme.palette.text.primary }}>
                    {entry.payload.name}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={alpha(theme.palette.divider, 0.3)} 
              />
              <XAxis
                dataKey="name"
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                tickFormatter={(value) => 
                  viewMode === 'amount' ? `€${value}` : `${value}%`
                }
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey={viewMode === 'amount' ? 'amount' : 'percentage'}
                radius={[8, 8, 0, 0]}
                animationBegin={0}
                animationDuration={800}
                animationEasing="ease-out"
                cursor="pointer"
                onClick={(data) => handleMemberClick(data)}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* Toggle details button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          size="small"
          onClick={() => setShowDetails(!showDetails)}
          endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: 'none' }}
        >
          {showDetails ? 'Hide' : 'Show'} Member Details
        </Button>
      </Box>

      {/* Member summary cards with interactive legend */}
      <Collapse in={showDetails}>
        <Stack spacing={1} mt={2}>
          {spendingData.map((member, index) => {
            const isHidden = hiddenMembers.has(index);
            const memberColor = COLORS[index % COLORS.length];
            
            return (
              <Stack
                key={member.userId || index}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  p: 1.5,
                  backgroundColor: isHidden 
                    ? alpha(theme.palette.action.disabled, 0.05)
                    : alpha(memberColor, 0.1),
                  borderRadius: 1,
                  borderLeft: `4px solid ${isHidden ? theme.palette.action.disabled : memberColor}`,
                  opacity: isHidden ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                  cursor: onMemberClick ? 'pointer' : 'default',
                  '&:hover': {
                    backgroundColor: isHidden 
                      ? alpha(theme.palette.action.disabled, 0.1)
                      : alpha(memberColor, 0.15),
                    transform: onMemberClick ? 'translateX(4px)' : 'none'
                  }
                }}
                onClick={() => onMemberClick && handleMemberClick(member)}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
                  <Avatar
                    src={member.userAvatar}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: isHidden ? theme.palette.action.disabled : memberColor,
                      fontSize: '0.875rem',
                    }}
                  >
                    {(member.userName || 'U').charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography 
                    variant="body2" 
                    fontWeight="500"
                    sx={{ 
                      textDecoration: isHidden ? 'line-through' : 'none',
                      color: isHidden ? 'text.disabled' : 'text.primary'
                    }}
                  >
                    {member.userName || 'Unknown User'}
                  </Typography>
                </Stack>
                
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography 
                    variant="body2" 
                    fontWeight="600"
                    sx={{ color: isHidden ? 'text.disabled' : 'text.primary' }}
                  >
                    {formatCurrency(member.amount || 0)}
                  </Typography>
                  <Chip
                    label={`${totalSpending > 0 ? ((member.amount || 0) / totalSpending * 100).toFixed(1) : 0}%`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      backgroundColor: isHidden 
                        ? alpha(theme.palette.action.disabled, 0.2)
                        : alpha(memberColor, 0.2),
                      color: isHidden ? 'text.disabled' : 'text.primary',
                    }}
                  />
                  <MuiTooltip title={isHidden ? 'Show in chart' : 'Hide from chart'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMemberVisibility(index);
                      }}
                      sx={{ ml: 1 }}
                    >
                      {isHidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </MuiTooltip>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Collapse>
    </Paper>
  );
};

SpendingBreakdownChart.propTypes = {
  spendingData: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
      userName: PropTypes.string,
      userAvatar: PropTypes.string,
      amount: PropTypes.number,
    })
  ),
  totalBudget: PropTypes.number,
  onMemberClick: PropTypes.func,
};

export default SpendingBreakdownChart;
